import { EQuestionStatus, EQuestionType } from '@constants';
import { IQuestion } from '@sdk/contracts';
import { Checkbox, Tooltip } from 'antd';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';

import {
  ActionWrapper,
  CoursesWrapper,
  CourseWrapper,
  DescriptionWrapper,
  InfoWrapper,
  SubtitleWrapper,
  TitleWrapper,
  Wrapper
} from './QuestionItem.styles';

import AI_ICON from '@/assets/ai_ucademy_icon_gradient.svg';
import { EIconType, Icon } from '@/components/Icon';
import { LoadingCircleSpinner } from '@/components/Loaders';
import { useQuestionQuizzes } from '@/store';

const ACTION_OPTIONS: Record<
  EQuestionStatus,
  { icon: EIconType; color: keyof DefaultTheme['colors'] }
> = {
  [EQuestionStatus.HIDDEN]: {
    icon: 'ViewOff',
    color: 'black6'
  },
  [EQuestionStatus.REPORTED]: {
    icon: 'Flag',
    color: 'red7'
  },
  [EQuestionStatus.ACTIVE]: {
    icon: 'View',
    color: 'green7'
  }
};

type Props = {
  question: IQuestion;
  onQuestionClick: (question: IQuestion) => void;
  isSelected: boolean;
  checkable?: boolean;
};

export const QuestionItem: React.FC<Props> = ({
  question,
  onQuestionClick,
  isSelected,
  checkable = false
}) => {
  const { t } = useTranslation();
  const { quizzes, isPending } = useQuestionQuizzes(question._id);

  const courseNames = useMemo(() => {
    const courses = quizzes?.map(quiz => quiz.course.name);
    return courses ? uniqBy(courses, 'name') : [];
  }, [quizzes]);

  const { color, icon } = useMemo(() => {
    return ACTION_OPTIONS[question.status];
  }, [question.status]);

  return (
    <Wrapper $isSelected={isSelected} onClick={() => onQuestionClick(question)}>
      {checkable && <Checkbox checked={isSelected} />}
      <InfoWrapper>
        <TitleWrapper>{question.title}</TitleWrapper>
        <SubtitleWrapper>
          {question.type === EQuestionType.AI && (
            <>
              <Tooltip title={t('AI_QUESTION_TOOLTIP')}>
                <img src={AI_ICON} alt="AI_icon" />
              </Tooltip>
              <span>{'·'}</span>
            </>
          )}
          {isPending ? (
            <LoadingCircleSpinner size={16} />
          ) : (
            <SubtitleWrapper>
              <DescriptionWrapper>
                {t('QUIZZES_COUNT', { count: quizzes?.length || 0 })}
              </DescriptionWrapper>
              <span>{'·'}</span>
              <CoursesWrapper>
                {courseNames.map((course, index) => {
                  const isLastItem = index === courseNames.length - 1;
                  return (
                    <CourseWrapper key={course}>
                      {course}
                      {!isLastItem && <span>{' | '}</span>}
                    </CourseWrapper>
                  );
                })}
              </CoursesWrapper>
            </SubtitleWrapper>
          )}
        </SubtitleWrapper>
      </InfoWrapper>
      <ActionWrapper>
        <Icon color={color} i={icon} />
      </ActionWrapper>
    </Wrapper>
  );
};
